// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyLangValue, TyLead, TyLeadStage } from "@/types";
import { formatDate, leadTitle, pressToOpen, readLangValue, rightClickToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import AvatarGroup from "@/UI/atoms/AvatarGroup";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import LeadStepper from "@/UI/components/LeadStepper";
import "../selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    lead: TyLead;
    onOpen: () => void;
    /** Logs a call against this enquiry. Absent on a closed one, which takes no interactions. */
    onContact?: () => void;
    /** The row's menu. Absent on a read-only list — a property's "who wants this" tab. */
    onActions?: (anchor: DOMRect) => void;
    /** Moves the deal. Absent leaves the trail a picture rather than a control. */
    onStage?: (stage: TyLeadStage) => void;
    /** On a customer's own screen the person is already the headline, so the card drops their name. */
    hidePerson?: boolean;
    /** Picked by the bulk bar. Draws the ring; the screen decides what pressing the card then means. */
    isSelected?: boolean;
    /**
     * The bar is up. Every other control on the card is **gone, not disabled** — a half-live card
     * looks pressable and does something nobody asked for — and the menu button's place is taken by
     * the tick, so nothing moves and nothing overlaps.
     */
    isPicking?: boolean;
};

/**
 * One enquiry — the same card in the pipeline and on a person's own screen, because it is the same
 * thing being looked at from two directions.
 *
 * The person comes first and what they want second, which is the order a sales agent thinks in: you
 * remember a name, then what they rang about.
 *
 * **The whole card opens it**, like every list in the app. The two buttons at the foot are the only
 * things that mean something else, and both stop the click.
 *
 * The foot is three facts and no captions. "Last contact", "interactions" and "assignees" written
 * out took four lines and most of the card's width to say three short things, and a pipeline is
 * read by scanning rather than by reading. So: a clock, a handset, and the faces themselves — and
 * the budget is gone entirely, because it was the widest value here and the one nobody scans for.
 *
 * The source is a caption under what they want rather than a chip among chips. Where somebody came
 * from is context for the enquiry, not a status of it.
 *
 * **The stage is the trail, on the card.** A chip said one word; the trail says the same word and
 * the distance left, which is what a pipeline is read for — and it can be pressed, so moving a deal
 * costs one tap from the list instead of a menu, a panel and a save. Every move is confirmed inside
 * the stepper, and illegal ones are dead rather than refused.
 */
export default function LeadCard({ lead, onOpen, onContact, onActions, onStage, hidePerson, isSelected, isPicking }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const customer = typeof lead.customer === "object" && lead.customer ? lead.customer : null;
    const title = leadTitle(lead, t, (value) => readLangValue(value as TyLangValue[], lang));
    const holders = lead.assignees
        .map((entry) =>
            typeof entry.client === "object" ? { _id: entry.client._id, name: entry.client.name, phone: entry.client.phone ?? null } : null
        )
        .filter((entry): entry is { _id: string; name: string; phone: string | null } => !!entry);

    return (
        <article
            className={`lead-card pick${isSelected ? " pick--on" : ""}${isPicking ? " pick--picking" : ""}`}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={isPicking || !onActions ? undefined : rightClickToOpen(onActions)}
        >
            {!hidePerson && customer && (
                <div className="lead-card__who">
                    <Avatar name={customer.name} picture={customer.picture ?? null} size={38} bordered />
                    <span className="lead-card__identity">
                        <span className="lead-card__name">{customer.name}</span>
                        {/* A phone number reads left to right in every language, so it carries its
                            own direction rather than inheriting the page's. */}
                        <bdi className="lead-card__phone" dir="ltr">
                            {customer.phone}
                        </bdi>
                    </span>
                </div>
            )}

            <div className="lead-card__want">
                <p className={`lead-card__title${hidePerson ? " lead-card__title--lead" : ""}`}>{title}</p>
                {lead.source && (
                    <span className="lead-card__source">
                        {t.crm.fields.source} · {t.crm.sources[lead.source]}
                    </span>
                )}
            </div>

            {(lead.closed || customer?.isBlocked) && (
                <div className="lead-card__chips">
                    {lead.closed && <StatusChip size="sm" tone="neutral" label={t.crm.closedBadge} />}
                    {customer?.isBlocked && <StatusChip size="sm" tone="danger" label={t.crm.blocked} />}
                </div>
            )}

            {/* A picture rather than a control while the bar is up: one tap on a stage in the middle
                of selecting twenty would move that one deal and nothing else, which nobody meant. */}
            <div className="lead-card__stage">
                <LeadStepper lead={lead} onPick={isPicking ? undefined : onStage} />
            </div>

            {/* Three facts, no captions — see the note above. */}
            <div className="lead-card__meta">
                <span className="lead-card__fact" title={t.crm.lastContact}>
                    <Icon name="history" size={14} />
                    {lead.lastContactedAt ? formatDate(lead.lastContactedAt, lang) : t.crm.never}
                </span>

                <span className="lead-card__fact" title={t.crm.counting.interactions}>
                    <Icon name="phone" size={14} />
                    {lead.interactionsCount}
                </span>

                <span className="lead-card__holders">
                    <AvatarGroup people={holders} size={24} max={3} empty={t.crm.unassigned} />
                </span>
            </div>

            <div className="lead-card__actions">
                {isPicking && (
                    <span className={`pick__box${isSelected ? " pick__box--on" : ""}`} aria-hidden="true">
                        <Icon name="check" size={18} />
                    </span>
                )}

                {!isPicking && onContact && (
                    <button
                        type="button"
                        className="lead-card__action"
                        onClick={(event) => {
                            event.stopPropagation();
                            onContact();
                        }}
                    >
                        {t.crm.actions.contact}
                    </button>
                )}

                {/* Behind the primary rather than above the card: the two things that are not
                    "open this" belong together, at the end of the row the eye finishes on. */}
                {!isPicking && onActions && (
                    <button
                        type="button"
                        className="lead-card__more"
                        onClick={(event) => {
                            event.stopPropagation();
                            onActions(event.currentTarget.getBoundingClientRect());
                        }}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>
        </article>
    );
}
